import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { color } from '../styles/theme';
import { dateTime, fullDate } from '../lib/format';
import { fieldErrorsFrom } from '../lib/fieldErrors';
import { ApiError, type MeetingDetailResponse, type MeetingItem, type MeetingItemKind, type MeetingPerson } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import {
  KIND_LABEL,
  useAddItem,
  useDraftSummary,
  useMeeting,
  useMeetingPeople,
  useRemoveItem,
  useSendSummary,
  useSetPhase,
  useSuggestAgenda,
  useUpdateItem,
  useUpdateMeeting,
  type AskTurn,
} from '../api/meetings';
import { SectionTabs } from '../components/SectionTabs';
import { Button, Card, CardTitle, Confirm, ErrorNote, Field, Loading, Modal, PageTitle, inputStyle } from '../components/ui';
import {
  AboutPanel,
  ActionHead,
  ActionRow,
  CarriedPanel,
  KindPill,
  PersonSelect,
  PhasePill,
  PhaseSteps,
  isLate,
  whenLabel,
  type ContinueSource,
} from './MeetingBits';
import { NewMeeting } from './NewMeeting';
import { AskEna } from './MeetingAssistant';

/**
 * One meeting, as a page: a live meeting needs the room, and a dialog that
 * closes on Escape is the wrong place to type for an hour.
 *
 * The body follows the phase. Preparing: the agenda, grouped by who added
 * it, plus what carried over. In meeting: the agenda to tick off beside the
 * live notes. Done: the summary, the action items, and the send.
 */

type Detail = MeetingDetailResponse;

const small: CSSProperties = { padding: '5px 11px', fontSize: 12 };
const muted: CSSProperties = { fontSize: 12.5, color: color.muted, margin: 0 };
const amberNote: CSSProperties = {
  fontSize: 12.5,
  margin: 0,
  padding: '8px 12px',
  borderRadius: 10,
  background: '#f4f0e6',
  color: '#7a6234',
};
const linkButton: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 12.5,
  fontWeight: 700,
  color: color.blue,
  textAlign: 'left',
};

const NOT_CONFIGURED = 'Ena is not configured on this API.';
const RECORD_ID_HINT = 'Refer to a participant by record ID, never by name.';

export function MeetingPage({ id, onBack, onOpen }: { id: string; onBack: () => void; onOpen: (id: string) => void }) {
  const { can, admin } = useAuth();
  const canUpdate = can('meetings:update');
  const detail = useMeeting(id);
  const people = useMeetingPeople(canUpdate);
  const setPhase = useSetPhase();
  /** Everyone in the room adds agenda items in their own name; the rest of the writing needs the update tick. */
  const isAttendee = !!admin && !!detail.data?.attendees.some((a) => a.id === admin.id);
  const canAgenda = canUpdate || isAttendee;
  const [editing, setEditing] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  /** The conversation with Ena, kept while the meeting is open on screen. */
  const [askThread, setAskThread] = useState<AskTurn[]>([]);
  const [confirmPhase, setConfirmPhase] = useState<'in_meeting' | 'done' | null>(null);

  const back = (
    <button onClick={onBack} style={{ ...linkButton, fontSize: 13 }}>
      ← Meetings
    </button>
  );

  if (detail.isLoading && !detail.data) return <Loading label="Opening the meeting…" />;
  if (detail.error) {
    const gone = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {back}
        <ErrorNote message={gone ? 'That meeting is no longer there.' : (detail.error as Error).message} />
      </div>
    );
  }
  if (!detail.data) return null;

  const d = detail.data;
  const m = d.meeting;
  const peopleList = people.data?.people ?? [];
  const noOwner = d.items.filter((i) => i.kind === 'action' && i.status === 'open' && !i.ownerId).length;

  const source: ContinueSource = {
    id: m.id,
    title: m.title,
    phase: m.phase,
    scheduledAt: m.scheduledAt,
    attendeeIds: d.attendees.map((a) => a.id),
    carry: {
      actions:
        d.items.filter((i) => i.kind === 'action' && i.status === 'open').length +
        d.carried.actions.filter((a) => a.status === 'open').length,
      takeaways: d.items.filter((i) => i.kind === 'takeaway').length,
      questions:
        d.items.filter((i) => i.kind === 'question' && !i.answeredAt).length +
        d.carried.questions.filter((q) => !q.answeredAt).length,
    },
  };

  const move = (phase: 'in_meeting' | 'done') => {
    setConfirmPhase(null);
    setPhase.mutate({ id: m.id, phase });
  };

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {back}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <PageTitle sub={`${whenLabel(m.scheduledAt)} · ${d.attendees.length} in the room: ${d.attendees.map((a) => a.name).join(', ') || 'nobody yet'}`}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {m.title}
                <PhasePill phase={m.phase} />
              </span>
            </PageTitle>
            {(d.previous || d.next.length > 0) && (
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
                {d.previous && (
                  <button onClick={() => onOpen(d.previous!.id)} style={linkButton} className="hov-underline">
                    ← Continues {d.previous.title}
                    {d.previous.scheduledAt ? ` · ${fullDate(d.previous.scheduledAt)}` : ''}
                  </button>
                )}
                {d.next.map((n) => (
                  <button key={n.id} onClick={() => onOpen(n.id)} style={linkButton} className="hov-underline">
                    Continued by {n.title}
                    {n.scheduledAt ? ` · ${fullDate(n.scheduledAt)}` : ''} →
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="soft" onClick={() => setAskOpen(true)} title="Ena answers from this meeting's own lines">
              ✦ Ask Ena
            </Button>
            {canUpdate && (
              <>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                Edit
              </Button>
              {m.phase === 'preparing' && (
                <Confirm
                  open={confirmPhase === 'in_meeting'}
                  title="Start the meeting?"
                  detail="The agenda stays open; notes, questions, takeaways and action items can be added from now on."
                  confirmLabel="Start"
                  align="right"
                  onConfirm={() => move('in_meeting')}
                  onCancel={() => setConfirmPhase(null)}
                >
                  <Button variant="navy" onClick={() => setConfirmPhase('in_meeting')} disabled={setPhase.isPending}>
                    Start meeting
                  </Button>
                </Confirm>
              )}
              {m.phase === 'in_meeting' && (
                <Confirm
                  open={confirmPhase === 'done'}
                  title="End the meeting?"
                  detail={
                    noOwner > 0
                      ? `${noOwner} action item${noOwner === 1 ? ' has' : 's have'} no owner yet. You can still assign them afterwards.`
                      : 'Next comes the summary, then the send to everyone in the room.'
                  }
                  confirmLabel="End meeting"
                  align="right"
                  onConfirm={() => move('done')}
                  onCancel={() => setConfirmPhase(null)}
                >
                  <Button variant="navy" onClick={() => setConfirmPhase('done')} disabled={setPhase.isPending}>
                    End meeting
                  </Button>
                </Confirm>
              )}
              {m.phase === 'done' && !m.sentAt && (
                <Button variant="ghost" onClick={() => move('in_meeting')} disabled={setPhase.isPending} title="Ended too early? Nothing has been sent yet.">
                  Reopen
                </Button>
              )}
              {m.phase === 'done' && (
                <Button variant="navy" onClick={() => setContinuing(true)}>
                  Continue this meeting
                </Button>
              )}
              </>
            )}
          </div>
        </div>
        {setPhase.error && <ErrorNote message={(setPhase.error as Error).message} />}
      </div>

      <Card pad="16px 20px 14px">
        <PhaseSteps meeting={m} items={d.items} />
      </Card>

      {m.phase === 'preparing' && (
        <AboutPanel storageKey="enact.meetings.about.preparing" title="Preparing">
          Everyone in the room adds the agenda items they want to talk about — by hand, or from Ena's suggestions,
          built on what carried over. Press <strong>Start meeting</strong> when you are in the room. {RECORD_ID_HINT}
        </AboutPanel>
      )}
      {m.phase === 'in_meeting' && (
        <AboutPanel storageKey="enact.meetings.about.in_meeting" title="In the room">
          Tick agenda items off as you cover them and type what happens: a note, a question, a takeaway, or an action
          item with an owner and a due date. Last week's action items can be settled from here. Everyone in the room sees
          each other's lines within a few seconds. {RECORD_ID_HINT}
        </AboutPanel>
      )}
      {m.phase === 'done' && (
        <AboutPanel storageKey="enact.meetings.about.done" title="Done">
          Write the summary, or let Ena draft one from the notes and edit it, then send it to everyone in the room
          with their action items. <strong>Continue this meeting</strong> starts next week's with what is still open.
        </AboutPanel>
      )}

      {m.phase === 'preparing' && <Preparing d={d} canUpdate={canUpdate} canAgenda={canAgenda} people={peopleList} meId={admin?.id ?? null} />}
      {m.phase === 'in_meeting' && <InRoom d={d} canUpdate={canUpdate} canAgenda={canAgenda} people={peopleList} />}
      {m.phase === 'done' && <Done d={d} canUpdate={canUpdate} people={peopleList} />}

      {editing && <EditMeeting d={d} people={peopleList} meId={admin?.id ?? null} onClose={() => setEditing(false)} />}
      {askOpen && <AskEna d={d} thread={askThread} setThread={setAskThread} onClose={() => setAskOpen(false)} />}
      {continuing && (
        <NewMeeting
          continuesFrom={source}
          onClose={() => setContinuing(false)}
          onCreated={(next) => {
            setContinuing(false);
            onOpen(next);
          }}
        />
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── composer */

/**
 * The box everything is typed into. Kinds switch above it when more than
 * one is offered; an action item also takes an owner and a due date.
 * Ctrl/⌘-Enter adds, so a note in the room is one keystroke away.
 */
function Composer({
  meetingId,
  kinds,
  people,
  attendees,
  placeholder,
  buttonLabel = 'Add',
}: {
  meetingId: string;
  kinds: readonly MeetingItemKind[];
  people: ReadonlyArray<MeetingPerson>;
  attendees: ReadonlyArray<{ id: string }>;
  placeholder?: string;
  buttonLabel?: string;
}) {
  const add = useAddItem();
  const [kind, setKind] = useState<MeetingItemKind>(kinds[0] ?? 'note');
  const [body, setBody] = useState('');
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [dueOn, setDueOn] = useState('');

  useEffect(() => {
    if (!kinds.includes(kind)) setKind(kinds[0] ?? 'note');
  }, [kinds, kind]);

  const ready = body.trim().length > 0 && !add.isPending;

  async function submit() {
    if (!ready) return;
    try {
      await add.mutateAsync({
        id: meetingId,
        kind,
        body: body.trim(),
        ...(kind === 'action' ? { ownerId, dueOn: dueOn || null } : {}),
      });
      setBody('');
      setOwnerId(null);
      setDueOn('');
    } catch {
      /* surfaced through add.error */
    }
  }

  const hint =
    kind === 'agenda'
      ? 'What do you want to talk about?'
      : kind === 'question'
        ? 'What needs an answer?'
        : kind === 'takeaway'
          ? 'What did we learn or decide?'
          : kind === 'action'
            ? 'What has to happen, and by whom?'
            : 'What happened, in a line or two.';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {kinds.length > 1 && (
        <SectionTabs value={kind} onChange={setKind} compact tabs={kinds.map((k) => ({ key: k, label: KIND_LABEL[k] }))} />
      )}
      <textarea
        value={body}
        rows={kind === 'agenda' ? 2 : 3}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void submit();
          }
        }}
        placeholder={placeholder ?? hint}
        aria-label={KIND_LABEL[kind]}
        style={{ ...inputStyle, fontSize: 14, lineHeight: '21px', resize: 'vertical' }}
      />
      {kind === 'action' && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <PersonSelect
            label="Owner"
            value={ownerId}
            currentName={null}
            people={people}
            attendees={attendees}
            onChange={setOwnerId}
          />
          <input
            type="date"
            aria-label="Due date"
            value={dueOn}
            onChange={(e) => setDueOn(e.target.value)}
            style={{ ...inputStyle, width: 'auto', fontSize: 12.5, padding: '6px 10px' }}
          />
          <span style={muted}>An action item is an owner, a due date and done. Both can come later.</span>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="navy" onClick={() => void submit()} disabled={!ready} title={ready ? 'Ctrl/⌘-Enter adds it too' : 'Write something first.'}>
          {add.isPending ? 'Adding…' : buttonLabel}
        </Button>
        <span style={muted}>{RECORD_ID_HINT}</span>
      </div>
      {add.error && <ErrorNote message={(add.error as Error).message} />}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── one line */

function ItemHeader({ item, canRemove, onRemove, removing }: { item: MeetingItem; canRemove: boolean; onRemove: () => void; removing: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <KindPill kind={item.kind} />
      <span style={{ fontSize: 11.5, color: color.muted, flex: 1 }}>
        {item.authorName ?? 'Someone'} · {dateTime(item.createdAt)}
        {item.source === 'ai' ? " · from Ena's suggestion" : item.source === 'carried' ? ' · from what carried over' : ''}
      </span>
      {canRemove && (
        <Button variant="ghost" style={small} onClick={onRemove} disabled={removing} title="Remove this line">
          Remove
        </Button>
      )}
    </div>
  );
}

/** A note, question or takeaway as written, with its controls. Actions use ActionRow. */
function LogLine({
  item,
  d,
  canUpdate,
  people,
}: {
  item: MeetingItem;
  d: Detail;
  canUpdate: boolean;
  people: ReadonlyArray<MeetingPerson>;
}) {
  const update = useUpdateItem();
  const remove = useRemoveItem();
  const editable = canUpdate && d.meeting.phase !== 'done';
  const m = d.meeting;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '12px 0', borderTop: `1px solid ${color.line}` }}>
      <ItemHeader item={item} canRemove={editable} removing={remove.isPending} onRemove={() => remove.mutate({ id: m.id, itemId: item.id })} />
      {item.kind === 'action' ? (
        <ActionRow item={item} meetingId={m.id} canWrite={canUpdate} people={people} attendees={d.attendees} layout="stack" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
          <div
            style={{
              fontSize: 14,
              lineHeight: '21px',
              color: item.kind === 'question' && item.answeredAt ? color.muted : color.ink,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            {item.body}
          </div>
          {item.kind === 'question' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {item.answeredAt && <span style={{ fontSize: 12, fontWeight: 700, color: color.green }}>Answered</span>}
              {canUpdate && (
                <Button
                  variant={item.answeredAt ? 'ghost' : 'soft'}
                  style={small}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ id: m.id, itemId: item.id, answered: !item.answeredAt })}
                >
                  {item.answeredAt ? 'Reopen' : 'Mark answered'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {(update.error || remove.error) && <ErrorNote message={((update.error ?? remove.error) as Error).message} />}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── preparing */

function Preparing({
  d,
  canUpdate,
  canAgenda,
  people,
  meId,
}: {
  d: Detail;
  canUpdate: boolean;
  canAgenda: boolean;
  people: ReadonlyArray<MeetingPerson>;
  meId: string | null;
}) {
  const m = d.meeting;
  const add = useAddItem();
  const remove = useRemoveItem();
  const agenda = d.items.filter((i) => i.kind === 'agenda');

  /** Grouped by who added it, the reader first. */
  const groups = useMemo(() => {
    const byAuthor = new Map<string, { name: string; items: MeetingItem[] }>();
    for (const i of agenda) {
      const key = i.authorId ?? '';
      const g = byAuthor.get(key) ?? { name: i.authorName ?? 'Someone', items: [] };
      g.items.push(i);
      byAuthor.set(key, g);
    }
    return [...byAuthor.entries()]
      .sort(([a], [b]) => (a === meId ? -1 : b === meId ? 1 : 0))
      .map(([key, g]) => ({ key, ...g }));
  }, [agenda, meId]);

  const addToAgenda = (item: MeetingItem) =>
    add.mutate({ id: m.id, kind: 'agenda', body: item.body, originItemId: item.id });

  return (
    <div className="meeting-cols">
      <Card>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
          <CardTitle>Agenda</CardTitle>
          <span style={muted}>
            {agenda.length === 0 ? 'nothing yet' : `${agenda.length} item${agenda.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {agenda.length === 0 && <p style={{ ...muted, marginBottom: 12 }}>No agenda yet. Add what you want to talk about.</p>}
        {groups.map((g) => (
          <section key={g.key} style={{ marginBottom: 12 }}>
            <h4 style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.muted, margin: '0 0 4px' }}>
              {g.key === meId ? 'You' : g.name}
            </h4>
            {g.items.map((i) => (
              <div key={i.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${color.line}`, fontSize: 14 }}>
                <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {i.body}
                  {i.source !== 'manual' && (
                    <span style={{ fontSize: 11.5, color: color.muted }}> · {i.source === 'ai' ? "from Ena's suggestion" : 'carried over'}</span>
                  )}
                </div>
                {(canUpdate || (canAgenda && i.authorId === meId)) && (
                  <Button variant="ghost" style={small} disabled={remove.isPending} onClick={() => remove.mutate({ id: m.id, itemId: i.id })}>
                    Remove
                  </Button>
                )}
              </div>
            ))}
          </section>
        ))}
        {remove.error && <ErrorNote message={(remove.error as Error).message} />}
        {add.error && <ErrorNote message={(add.error as Error).message} />}

        {canAgenda ? (
          <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <Composer meetingId={m.id} kinds={['agenda']} people={people} attendees={d.attendees} buttonLabel="Add to the agenda" />
            <Suggest d={d} />
          </div>
        ) : (
          <p style={muted}>Your role can read meetings but not add to them.</p>
        )}
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Attendees d={d} />
        {d.previous && (
          <CarriedPanel meeting={m} carried={d.carried} canWrite={canUpdate} people={people} attendees={d.attendees} onAddToAgenda={addToAgenda} />
        )}
      </div>
    </div>
  );
}

function Attendees({ d }: { d: Detail }) {
  return (
    <Card>
      <CardTitle style={{ marginBottom: 8 }}>In the room</CardTitle>
      {d.attendees.length === 0 && <p style={muted}>Nobody yet — Edit adds people.</p>}
      {d.attendees.map((a) => (
        <div key={a.id} style={{ fontSize: 13.5, color: color.ink, padding: '4px 0' }}>
          {a.name}
          <span style={{ fontSize: 12, color: color.muted }}> · {a.email}</span>
        </div>
      ))}
      {d.meeting.createdByName && <p style={{ ...muted, marginTop: 8 }}>Set up by {d.meeting.createdByName}.</p>}
    </Card>
  );
}

/**
 * The AI-assisted path. Nothing it returns is saved: each suggestion is
 * added by hand, as an ordinary agenda item marked as coming from the AI.
 */
function Suggest({ d }: { d: Detail }) {
  const m = d.meeting;
  const suggest = useSuggestAgenda();
  const add = useAddItem();
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [suggestions, setSuggestions] = useState<Array<{ body: string; why: string }>>([]);
  const configured = d.providers.ai.configured;

  async function run() {
    setConfirming(false);
    try {
      const res = await suggest.mutateAsync({ id: m.id, notes: notes.trim() });
      setSuggestions(res.suggestions);
    } catch {
      /* surfaced through suggest.error */
    }
  }

  async function accept(s: { body: string; why: string }) {
    await add.mutateAsync({ id: m.id, kind: 'agenda', body: s.body, source: 'ai' });
    setSuggestions((list) => list.filter((x) => x !== s));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <strong style={{ fontSize: 13.5, color: color.navy }}>Suggest with Ena</strong>
        <p style={muted}>
          Ena turns what carried over and what you type into agenda lines to consider. Only this meeting's own text
          goes out; nothing is saved until you add a line.
        </p>
      </div>
      {!configured ? (
        <p style={amberNote}>{NOT_CONFIGURED}</p>
      ) : (
        <>
          <textarea
            value={notes}
            rows={3}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything on your mind for this meeting? (optional)"
            aria-label="Notes for Ena"
            style={{ ...inputStyle, fontSize: 14, lineHeight: '21px', resize: 'vertical' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Confirm
              open={confirming}
              title="Let Ena read this meeting?"
              detail="Its agenda, what carried over, and what you typed — never a record. The call is logged."
              confirmLabel="Suggest"
              onConfirm={() => void run()}
              onCancel={() => setConfirming(false)}
            >
              <Button variant="soft" onClick={() => setConfirming(true)} disabled={suggest.isPending}>
                {suggest.isPending ? 'Ena is thinking…' : 'Ask Ena for agenda items'}
              </Button>
            </Confirm>
            {suggestions.length > 1 && (
              <Button
                variant="ghost"
                disabled={add.isPending}
                onClick={() => {
                  void (async () => {
                    for (const s of [...suggestions]) await accept(s);
                  })();
                }}
              >
                Add all
              </Button>
            )}
          </div>
          {suggest.error && <ErrorNote message={(suggest.error as Error).message} />}
          {add.error && <ErrorNote message={(add.error as Error).message} />}
          {suggest.isSuccess && suggestions.length === 0 && !suggest.isPending && (
            <p style={muted}>Nothing to add — the agenda already covers what Ena saw.</p>
          )}
          {suggestions.map((s) => (
            <div key={s.body} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '8px 12px', borderRadius: 12, background: color.bg }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, color: color.ink }}>{s.body}</div>
                {s.why && <div style={{ fontSize: 12, color: color.muted }}>{s.why}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="soft" style={small} disabled={add.isPending} onClick={() => void accept(s)}>
                  Add
                </Button>
                <Button variant="ghost" style={small} onClick={() => setSuggestions((list) => list.filter((x) => x !== s))}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── in meeting */

const ROOM_KINDS: readonly MeetingItemKind[] = ['note', 'question', 'takeaway', 'action'];

function InRoom({
  d,
  canUpdate,
  canAgenda,
  people,
}: {
  d: Detail;
  canUpdate: boolean;
  canAgenda: boolean;
  people: ReadonlyArray<MeetingPerson>;
}) {
  const m = d.meeting;
  const update = useUpdateItem();
  const add = useAddItem();
  const agenda = d.items.filter((i) => i.kind === 'agenda');
  const covered = agenda.filter((i) => i.coveredAt).length;
  const log = d.items.filter((i) => i.kind !== 'agenda');

  return (
    <div className="meeting-room">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
            <CardTitle>Agenda</CardTitle>
            <span style={muted}>{agenda.length === 0 ? 'nothing on it' : `${covered} of ${agenda.length} covered`}</span>
          </div>
          {agenda.map((i) => (
            <label key={i.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderTop: `1px solid ${color.line}`, fontSize: 14, cursor: canUpdate ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={!!i.coveredAt}
                disabled={!canUpdate || update.isPending}
                onChange={(e) => update.mutate({ id: m.id, itemId: i.id, covered: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: color.blue, marginTop: 3, flex: 'none' }}
              />
              <span style={{ color: i.coveredAt ? color.muted : color.ink, textDecoration: i.coveredAt ? 'line-through' : 'none', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {i.body}
                {i.authorName && <span style={{ fontSize: 11.5, color: color.muted }}> · {i.authorName}</span>}
              </span>
            </label>
          ))}
          {update.error && <ErrorNote message={(update.error as Error).message} />}
          {canAgenda && (
            <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 12, marginTop: 4 }}>
              <Composer meetingId={m.id} kinds={['agenda']} people={people} attendees={d.attendees} placeholder="Something else to cover?" buttonLabel="Add" />
            </div>
          )}
        </Card>
        {d.previous && (
          <CarriedPanel
            meeting={m}
            carried={d.carried}
            canWrite={canUpdate}
            people={people}
            attendees={d.attendees}
            compact
            onAddToAgenda={(item) => add.mutate({ id: m.id, kind: 'agenda', body: item.body, originItemId: item.id })}
          />
        )}
      </div>

      <Card>
        <CardTitle style={{ marginBottom: 10 }}>Live notes</CardTitle>
        {canUpdate ? (
          <Composer meetingId={m.id} kinds={ROOM_KINDS} people={people} attendees={d.attendees} />
        ) : (
          <p style={muted}>
            {canAgenda
              ? 'You can add to the agenda; the notes in the room are written by someone with the update permission.'
              : 'Your role can read meetings but not write in them.'}
          </p>
        )}
        <div style={{ marginTop: 16 }}>
          {log.length === 0 ? (
            <p style={muted}>Nothing written yet.</p>
          ) : (
            log.map((i) => <LogLine key={i.id} item={i} d={d} canUpdate={canUpdate} people={people} />)
          )}
        </div>
      </Card>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────── done */

function Done({ d, canUpdate, people }: { d: Detail; canUpdate: boolean; people: ReadonlyArray<MeetingPerson> }) {
  const m = d.meeting;
  const actions = d.items.filter((i) => i.kind === 'action');
  const carriedOpen = d.carried.actions.filter((a) => a.status === 'open');
  const takeaways = d.items.filter((i) => i.kind === 'takeaway');
  const questions = d.items.filter((i) => i.kind === 'question');
  const notes = d.items.filter((i) => i.kind === 'note');
  const late = [...actions, ...carriedOpen].filter(isLate).length;

  return (
    <div className="meeting-cols">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Summary d={d} canUpdate={canUpdate} />

        <Card>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
            <CardTitle>Action items</CardTitle>
            <span style={muted}>
              {actions.length === 0 && carriedOpen.length === 0
                ? 'none'
                : `${actions.filter((a) => a.status === 'open').length + carriedOpen.length} open`}
            </span>
            {late > 0 && (
              <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999, background: '#fdf1ee', color: '#a4432f' }}>
                {late} late
              </span>
            )}
          </div>
          {actions.length === 0 && carriedOpen.length === 0 ? (
            <p style={muted}>No action items came out of this meeting.</p>
          ) : (
            <>
              <ActionHead from={carriedOpen.length > 0} />
              {actions.map((a) => (
                <ActionRow key={a.id} item={a} meetingId={m.id} canWrite={canUpdate} people={people} attendees={d.attendees} />
              ))}
              {carriedOpen.map((a) => (
                <ActionRow key={a.id} item={a} meetingId={m.id} canWrite={canUpdate} people={people} attendees={d.attendees} showFrom />
              ))}
            </>
          )}
          {canUpdate && (
            <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 12, marginTop: 8 }}>
              <Composer meetingId={m.id} kinds={['action']} people={people} attendees={d.attendees} placeholder="Something that was decided but not written down?" buttonLabel="Add an action item" />
            </div>
          )}
        </Card>

        <Card>
          <CardTitle style={{ marginBottom: 4 }}>Takeaways</CardTitle>
          {takeaways.length === 0 ? <p style={muted}>None written.</p> : takeaways.map((i) => <LogLine key={i.id} item={i} d={d} canUpdate={canUpdate} people={people} />)}
        </Card>

        <Card>
          <CardTitle style={{ marginBottom: 4 }}>Questions</CardTitle>
          {questions.length === 0 ? <p style={muted}>None raised.</p> : questions.map((i) => <LogLine key={i.id} item={i} d={d} canUpdate={canUpdate} people={people} />)}
        </Card>

        <Card>
          <CardTitle style={{ marginBottom: 4 }}>Notes</CardTitle>
          {notes.length === 0 ? <p style={muted}>None written.</p> : notes.map((i) => <LogLine key={i.id} item={i} d={d} canUpdate={canUpdate} people={people} />)}
          {canUpdate && (
            <div style={{ borderTop: `1px solid ${color.line}`, paddingTop: 12, marginTop: 8 }}>
              <Composer meetingId={m.id} kinds={['note', 'question', 'takeaway']} people={people} attendees={d.attendees} placeholder="Something remembered after the room emptied?" />
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Attendees d={d} />
        {d.previous && <CarriedPanel meeting={m} carried={d.carried} canWrite={canUpdate} people={people} attendees={d.attendees} compact />}
      </div>
    </div>
  );
}

/** The summary: written, or drafted by the AI and edited; then sent. */
function Summary({ d, canUpdate }: { d: Detail; canUpdate: boolean }) {
  const m = d.meeting;
  const update = useUpdateMeeting();
  const draft = useDraftSummary();
  const send = useSendSummary();
  const [text, setText] = useState(m.summary ?? '');
  const [drafted, setDrafted] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sent, setSent] = useState<string | null>(null);

  // A save elsewhere (or a poll) brings a new summary; take it unless there is an unsaved edit.
  useEffect(() => {
    setText((t) => (t.trim() === '' || t === m.summary ? (m.summary ?? '') : t));
  }, [m.summary]);

  const dirty = text !== (m.summary ?? '');
  const hasSummary = !!m.summary?.trim();
  const configured = d.providers.ai.configured;
  const emailOn = d.providers.email.configured;

  async function save() {
    await update.mutateAsync({ id: m.id, summary: text, summarySource: drafted ? 'ai' : 'manual' });
    setDrafted(false);
  }

  async function runDraft() {
    setConfirmDraft(false);
    try {
      const res = await draft.mutateAsync({ id: m.id });
      setText(res.summary);
      setDrafted(true);
    } catch {
      /* surfaced through draft.error */
    }
  }

  async function runSend() {
    setConfirmSend(false);
    setSent(null);
    try {
      const res = await send.mutateAsync({ id: m.id });
      const n = res.sends.length;
      const failed = res.sends.filter((s) => s.status === 'failed');
      setSent(
        failed.length === n
          ? 'Nothing went out: every send failed.'
          : emailOn
            ? `Sent to ${n - failed.length} ${n - failed.length === 1 ? 'person' : 'people'}.`
            : `Logged for ${n} ${n === 1 ? 'person' : 'people'} — not delivered: no email provider is configured on this API.`,
      );
    } catch {
      /* surfaced through send.error */
    }
  }

  /** The latest batch: rows written within the same minute as the newest one. */
  const latest = useMemo(() => {
    const newest = d.sends[0];
    if (!newest) return null;
    const minute = newest.createdAt.slice(0, 16);
    const batch = d.sends.filter((s) => s.createdAt.slice(0, 16) === minute);
    return {
      at: newest.createdAt,
      count: batch.length,
      logged: batch.filter((s) => s.status === 'logged').length,
      failed: batch.filter((s) => s.status === 'failed'),
    };
  }, [d.sends]);

  const canSend = canUpdate && hasSummary && !dirty && !send.isPending;
  const sendWhy = !hasSummary ? 'Write or draft a summary first.' : dirty ? 'Save the summary first.' : undefined;

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <CardTitle>Summary</CardTitle>
        {m.summarySource === 'ai' && !dirty && <span style={muted}>Drafted by Ena, then saved as edited.</span>}
      </div>
      {canUpdate ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <textarea
            value={text}
            rows={10}
            onChange={(e) => setText(e.target.value)}
            placeholder={'What we covered\n- …\n\nDecisions and takeaways\n- …\n\nAction items\n- …\n\nOpen questions\n- …'}
            aria-label="Summary"
            style={{ ...inputStyle, fontSize: 14, lineHeight: '22px', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="navy" onClick={() => void save()} disabled={!dirty || update.isPending} title={dirty ? undefined : 'Nothing changed.'}>
              {update.isPending ? 'Saving…' : 'Save summary'}
            </Button>
            <Confirm
              open={confirmDraft}
              title="Let Ena draft the summary?"
              detail={text.trim() ? 'The draft replaces what is in the box; it is not saved until you press Save.' : 'From this meeting\'s notes only — never a record. The call is logged. Nothing is saved until you press Save.'}
              confirmLabel="Draft"
              onConfirm={() => void runDraft()}
              onCancel={() => setConfirmDraft(false)}
            >
              <Button variant="soft" onClick={() => setConfirmDraft(true)} disabled={!configured || draft.isPending} title={configured ? undefined : NOT_CONFIGURED}>
                {draft.isPending ? 'Ena is drafting…' : 'Draft with Ena'}
              </Button>
            </Confirm>
            {!configured && <span style={muted}>{NOT_CONFIGURED}</span>}
          </div>
          {update.error && <ErrorNote message={(update.error as Error).message} />}
          {draft.error && <ErrorNote message={(draft.error as Error).message} />}
        </div>
      ) : hasSummary ? (
        <p style={{ fontSize: 14, lineHeight: '22px', color: color.ink, whiteSpace: 'pre-wrap', margin: 0 }}>{m.summary}</p>
      ) : (
        <p style={muted}>No summary yet.</p>
      )}

      <div style={{ borderTop: `1px solid ${color.line}`, marginTop: 14, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {canUpdate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Confirm
              open={confirmSend}
              title={`Send the summary to ${d.attendees.length} ${d.attendees.length === 1 ? 'person' : 'people'}?`}
              detail={
                emailOn
                  ? 'Each person gets the summary, their own action items, and everyone else\'s.'
                  : 'No email provider is configured on this API, so the sends are logged, not delivered.'
              }
              confirmLabel={m.sentAt ? 'Send again' : 'Send'}
              onConfirm={() => void runSend()}
              onCancel={() => setConfirmSend(false)}
            >
              <Button variant="primary" onClick={() => setConfirmSend(true)} disabled={!canSend} title={sendWhy}>
                {send.isPending ? 'Sending…' : m.sentAt ? 'Send again' : 'Send to attendees'}
              </Button>
            </Confirm>
            {sent && <span style={{ fontSize: 12.5, fontWeight: 600, color: color.muted }}>{sent}</span>}
          </div>
        )}
        {!emailOn && canUpdate && <p style={amberNote}>No email provider on this API — a send is logged, not delivered.</p>}
        {send.error && <ErrorNote message={(send.error as Error).message} />}
        {latest ? (
          <p style={muted}>
            {latest.logged === latest.count ? 'Logged' : 'Sent'} {dateTime(latest.at)} {latest.logged === latest.count ? 'for' : 'to'} {latest.count}{' '}
            {latest.count === 1 ? 'person' : 'people'}
            {latest.failed.length > 0 && (
              <span style={{ color: color.red }}>
                {' '}
                · failed for {latest.failed.map((f) => f.name ?? f.email).join(', ')}
              </span>
            )}
            .
          </p>
        ) : (
          <p style={muted}>Not sent yet.</p>
        )}
      </div>
    </Card>
  );
}

/* ───────────────────────────────────────────────────────────────── edit */

const pad2 = (n: number) => String(n).padStart(2, '0');

function EditMeeting({ d, people, meId, onClose }: { d: Detail; people: ReadonlyArray<MeetingPerson>; meId: string | null; onClose: () => void }) {
  const m = d.meeting;
  const update = useUpdateMeeting();
  const start = m.scheduledAt ? new Date(m.scheduledAt) : null;
  const [title, setTitle] = useState(m.title);
  const [date, setDate] = useState(start ? `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}` : '');
  const [time, setTime] = useState(start ? `${pad2(start.getHours())}:${pad2(start.getMinutes())}` : '');
  const [attendees, setAttendees] = useState<Set<string>>(() => new Set(d.attendees.map((a) => a.id)));
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function save() {
    setErrors({});
    try {
      await update.mutateAsync({
        id: m.id,
        title: title.trim(),
        scheduledAt: date ? new Date(`${date}T${time || '09:00'}`).toISOString() : null,
        attendeeIds: [...attendees],
      });
      onClose();
    } catch (e) {
      if (e instanceof ApiError) setErrors(fieldErrorsFrom(e.details));
    }
  }

  const ready = title.trim().length > 0 && !update.isPending;

  return (
    <Modal
      label="Edit meeting"
      width={620}
      onClose={onClose}
      header={<span style={{ fontSize: 17, fontWeight: 700, color: color.navy }}>Edit meeting</span>}
      footer={
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
          {update.error && !(update.error instanceof ApiError && update.error.status === 400) && (
            <span style={{ flex: 1, minWidth: 200 }}>
              <ErrorNote message={(update.error as Error).message} />
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" onClick={() => void save()} disabled={!ready}>
            {update.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Title" error={errors.title}>
          <input value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Date">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Time">
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={inputStyle} disabled={!date} />
          </Field>
        </div>
        <Field label="Who is in the room" hint="Whoever set the meeting up stays in it." error={errors.attendeeIds}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '6px 12px' }}>
            {people.map((p) => {
              const fixed = p.id === m.createdById;
              return (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: color.ink }}>
                  <input
                    type="checkbox"
                    checked={fixed || attendees.has(p.id)}
                    disabled={fixed}
                    onChange={() =>
                      setAttendees((s) => {
                        const n = new Set(s);
                        if (n.has(p.id)) n.delete(p.id);
                        else n.add(p.id);
                        return n;
                      })
                    }
                    style={{ width: 15, height: 15, accentColor: color.blue }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.name}
                    {p.id === meId ? ' (you)' : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </Field>
      </div>
    </Modal>
  );
}
